import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Switch, Tabs, Space, message, InputNumber, Upload, Card, Tooltip, Image, Slider } from 'antd';
import { PlusOutlined, EditOutlined, UploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { PostSkin, PostArma, PostEscut, PostArc, PostFlecha, PostMagia } from '../../../dao/jutge/GetJavamonConfig';
import { getBase64, getImageUrl, javamonImage } from './javamonUtils';
import { RewardsUsageView } from './RewardsUsageView';

const { TabPane } = Tabs;

interface TabEquipmentProps {
    isAdmin: boolean;
    loading: boolean;
    skinsConfig: any;
    fetchSkinsConfig: () => Promise<void>;
    rewardsUsage: any;
    loadingRewardsUsage: boolean;
    fetchRewardsUsage: () => Promise<void>;
}

export const TabEquipment: React.FC<TabEquipmentProps> = ({
    isAdmin,
    loading,
    skinsConfig,
    fetchSkinsConfig,
    rewardsUsage,
    loadingRewardsUsage,
    fetchRewardsUsage
}) => {
    const [equipView, setEquipView] = useState<Record<string, 'table' | 'grid' | 'evo' | 'usage'>>({});
    const [dragEquipIdent, setDragEquipIdent] = useState<string | null>(null);
    const [dragOverEquipIdent, setDragOverEquipIdent] = useState<string | null>(null);
    const [savingEquip, setSavingEquip] = useState<boolean>(false);
    const [equipCardSize, setEquipCardSize] = useState<number>(64);

    const [equipmentModal, setEquipmentModal] = useState(false);
    const [equipmentType, setEquipmentType] = useState<string>('skin');
    const [editingItem, setEditingItem] = useState<any>(null);
    const [fileList, setFileList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [form] = Form.useForm();

    const postFnForType = (type: string) => {
        if (type === 'skin') return PostSkin;
        if (type === 'arma') return PostArma;
        if (type === 'escut') return PostEscut;
        if (type === 'arc') return PostArc;
        if (type === 'flecha') return PostFlecha;
        if (type === 'magia') return PostMagia;
        return PostSkin;
    };

    const equipListForType = (type: string): any[] => {
        if (type === 'skin') return skinsConfig.skins || [];
        if (type === 'arma') return skinsConfig.armes || [];
        if (type === 'escut') return skinsConfig.escuts || [];
        if (type === 'arc') return skinsConfig.arcs || [];
        if (type === 'flecha') return skinsConfig.flechas || [];
        if (type === 'magia') return skinsConfig.magias || [];
        return [];
    };

    const equipHasEvolution = (type: string) => ['arma', 'escut', 'arc'].includes(type);

    const handleToggleEquipmentActive = async (type: string, record: any, checked: boolean) => {
        try {
            const formData = new FormData();
            const body: any = {
                ...record,
                active: checked ? 1 : 0
            };
            delete body.imagen;
            formData.append('json', JSON.stringify(body));

            const postFn = postFnForType(type);
            const res = await postFn(formData);
            if (!res) throw new Error('Error');
            message.success("Estado actualizado");
            fetchSkinsConfig();
        } catch (e) {
            message.error("Error al actualizar estado");
        }
    };

    const handleSetEquipEvolution = async (type: string, source: any, targetIdent: string | null) => {
        if (!source) return;
        if (targetIdent && source.ident === targetIdent) {
            message.warning("Un elemento no puede evolucionar a sí mismo");
            return;
        }
        setSavingEquip(true);
        try {
            const body: any = { ...source, evoluciona_a: targetIdent || 'no evoluciona' };
            delete body.imagen;
            const formData = new FormData();
            formData.append('json', JSON.stringify(body));
            const res = await postFnForType(type)(formData);
            if (!res) throw new Error('Error');
            message.success(targetIdent ? `${source.nom} evoluciona a ${targetIdent}` : `${source.nom} ya no evoluciona`);
            fetchSkinsConfig();
        } catch (e) {
            message.error("Error al asignar la evolución");
        } finally {
            setSavingEquip(false);
        }
    };

    const handleSaveEquipment = async (values: any) => {
        try {
            const formData = new FormData();
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('file', fileList[0].originFileObj);
            }

            const body: any = {
                ...values,
                id: editingItem?.id || 0,
                active: values.active ? 1 : 0
            };

            if (fileList.length > 0 && fileList[0].originFileObj) {
                const b64 = await getBase64(fileList[0].originFileObj);
                body.imagen = b64;
            }

            formData.append('json', JSON.stringify(body));

            const postFn = postFnForType(equipmentType);
            const res = await postFn(formData);

            if (!res) throw new Error('Error');
            message.success("Guardado correctamente");
            setEquipmentModal(false);
            setFileList([]);
            setPreviewImage(null);
            fetchSkinsConfig();
        } catch (e) {
            message.error("Error al guardar");
        }
    };

    const openEquipmentModal = (type: string, record: any = null) => {
        setEquipmentType(type);
        setEditingItem(record);
        if (record) {
            const copy = { ...record };
            if (!copy.evoluciona_a) {
                const match = copy.ident?.match(/^(.*_)(\d+)$/);
                if (match) {
                    const base = match[1];
                    const num = parseInt(match[2], 10);
                    const nextIdent = base + (num + 1);

                    let exists = false;
                    if (type === 'arma') exists = skinsConfig.armes.some((x: any) => x.ident === nextIdent);
                    else if (type === 'escut') exists = skinsConfig.escuts.some((x: any) => x.ident === nextIdent);
                    else if (type === 'arc') exists = skinsConfig.arcs.some((x: any) => x.ident === nextIdent);

                    if (exists) {
                        copy.evoluciona_a = nextIdent;
                    } else {
                        copy.evoluciona_a = 'no evoluciona';
                    }
                } else {
                    copy.evoluciona_a = 'no evoluciona';
                }
            }
            if (copy.active !== undefined) copy.active = copy.active === 1;
            if (copy.es_escudo !== undefined) {
                if (copy.es_escudo === 1 || copy.es_escudo === true) copy.es_escudo = 'escudo';
                else if (copy.es_escudo === 0 || copy.es_escudo === false) copy.es_escudo = 'magia';
            } else {
                copy.es_escudo = 'magia';
            }
            form.setFieldsValue(copy);
        } else {
            form.resetFields();
        }
        setFileList([]);
        setPreviewImage(null);
        setEquipmentModal(true);
    };

    const activeColumn = (type: string) => ({
        title: 'Activo',
        dataIndex: 'active',
        key: 'active',
        render: (val: number, record: any) => (
            isAdmin ? (
                <Switch
                    checked={val === 1}
                    onChange={(checked) => handleToggleEquipmentActive(type, record, checked)}
                />
            ) : (
                <Tag color={val ? 'green' : 'red'}>{val ? 'SÍ' : 'NO'}</Tag>
            )
        )
    });

    const commonEquipmentColumns = [
        { title: 'Imagen', dataIndex: 'imagen', key: 'imagen', render: (_: any, record: any) => <Image src={javamonImage(record)} alt="img" width={50} /> },
        { title: 'Identificador (Unity)', dataIndex: 'ident', key: 'ident' },
        { title: 'Nombre', dataIndex: 'nom', key: 'nom' }
    ];

    const equipmentActions = (type: string) => ({
        title: 'Acciones', key: 'actions', render: (_: any, record: any) => (
            isAdmin ? <Button icon={<EditOutlined />} onClick={() => openEquipmentModal(type, record)} /> : null
        )
    });

    const evolutionColumn = {
        title: 'Evoluciona a',
        dataIndex: 'evoluciona_a',
        key: 'evoluciona_a',
        render: (val: string) => {
            if (!val || val === 'no evoluciona') return <span style={{ color: '#ccc' }}>No evoluciona</span>;
            return <Tag color="geekblue">{val}</Tag>;
        }
    };

    const columnsSkin = [
        ...commonEquipmentColumns,
        { title: 'Velocidad', dataIndex: 'velocitat', key: 'velocitat' },
        { title: 'Salud', dataIndex: 'salut', key: 'salut' },
        { title: 'Defensa', dataIndex: 'defensa', key: 'defensa' },
        { title: 'Clase', dataIndex: 'type', key: 'type', render: (val: string) => <Tag color={val === 'Guerrero' ? 'red' : val === 'Arquero' ? 'green' : val === 'Mago' ? 'blue' : val === 'Luchador' ? 'orange' : 'default'}>{val || 'Aventurero'}</Tag> },
        activeColumn('skin'),
        equipmentActions('skin')
    ];

    const columnsArma = [
        ...commonEquipmentColumns,
        { title: 'Daño', dataIndex: 'dany', key: 'dany' },
        { title: 'Crítico %', dataIndex: 'critico', key: 'critico' },
        { title: 'Velocidad', dataIndex: 'velocidad', key: 'velocidad' },
        evolutionColumn,
        activeColumn('arma'),
        equipmentActions('arma')
    ];

    const columnsEscut = [
        ...commonEquipmentColumns,
        { title: 'Defensa', dataIndex: 'defensa', key: 'defensa' },
        { title: 'Bloqueo %', dataIndex: 'bloqueo', key: 'bloqueo' },
        { title: 'Regeneración', dataIndex: 'regeneracion', key: 'regeneracion' },
        evolutionColumn,
        activeColumn('escut'),
        equipmentActions('escut')
    ];

    const columnsArc = [
        ...commonEquipmentColumns,
        { title: 'Daño', dataIndex: 'dany', key: 'dany' },
        { title: 'Rango', dataIndex: 'rang', key: 'rang' },
        { title: 'Velocidad', dataIndex: 'velocidad', key: 'velocidad' },
        evolutionColumn,
        activeColumn('arc'),
        equipmentActions('arc')
    ];

    const columnsFlecha = [
        ...commonEquipmentColumns,
        { title: 'Daño', dataIndex: 'dany', key: 'dany' },
        { title: 'Crítico %', dataIndex: 'critico', key: 'critico' },
        activeColumn('flecha'),
        equipmentActions('flecha')
    ];

    const columnsMagia = [
        ...commonEquipmentColumns,
        { title: 'Daño', dataIndex: 'dany', key: 'dany' },
        { title: 'Rango', dataIndex: 'rang', key: 'rang' },
        { title: 'Tipo', dataIndex: 'tipo', key: 'tipo', render: (val: any) => val || 'proyectil' },
        { title: 'Elemento', dataIndex: 'elemento', key: 'elemento', render: (val: any) => val ? <Tag color="blue">{val}</Tag> : <span style={{ color: '#ccc' }}>Ninguno</span> },
        { title: 'Tipo', dataIndex: 'es_escudo', key: 'es_escudo', render: (val: any) => (val === 'escudo' || val === 1 || val === true) ? <Tag color="green">Escudo</Tag> : val === 'golpe' ? <Tag color="red">Golpe</Tag> : <Tag color="blue">Magia</Tag> },
        activeColumn('magia'),
        equipmentActions('magia')
    ];

    const renderEquipCard = (type: string, item: any, options: { enableEvolutionDrop?: boolean; showRemove?: boolean; size?: number } = {}) => {
        const enableEvolutionDrop = options.enableEvolutionDrop === true && equipHasEvolution(type);
        const isDragging = dragEquipIdent === item.ident;
        const isDragOver = enableEvolutionDrop && dragOverEquipIdent === item.ident && dragEquipIdent !== item.ident;
        const hasEvo = item.evoluciona_a && item.evoluciona_a !== 'no evoluciona';
        const imgSize = options.size ?? 64;
        const cardWidth = imgSize + 36;

        const skinClassColor: Record<string, string> = {
            Guerrero: '#f5222d',
            Arquero: '#52c41a',
            Mago: '#1890ff',
            Luchador: '#fa8c16'
        };
        const baseBorderColor = (type === 'skin' && skinClassColor[item.type]) ? skinClassColor[item.type] : '#e0e0e0';

        return (
            <div
                key={item.id}
                data-equip-ident={item.ident}
                draggable={isAdmin && equipHasEvolution(type) && !savingEquip}
                onDragStart={(e) => { e.stopPropagation(); setDragEquipIdent(item.ident); }}
                onDragEnd={() => { setDragEquipIdent(null); setDragOverEquipIdent(null); }}
                onDragOver={enableEvolutionDrop ? (e) => { e.preventDefault(); if (dragOverEquipIdent !== item.ident) setDragOverEquipIdent(item.ident); } : undefined}
                onDragLeave={enableEvolutionDrop ? () => { if (dragOverEquipIdent === item.ident) setDragOverEquipIdent(null); } : undefined}
                onDrop={enableEvolutionDrop ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverEquipIdent(null);
                    if (dragEquipIdent && dragEquipIdent !== item.ident) {
                        const source = equipListForType(type).find((x: any) => x.ident === dragEquipIdent);
                        handleSetEquipEvolution(type, source, item.ident);
                    }
                    setDragEquipIdent(null);
                } : undefined}
                title={item.nom}
                style={{
                    width: cardWidth,
                    padding: 6,
                    borderRadius: 10,
                    border: `2px solid ${isDragOver ? '#1890ff' : baseBorderColor}`,
                    background: isDragOver ? '#e6f7ff' : '#fff',
                    boxShadow: isDragging ? '0 0 0 2px #1890ff' : '0 2px 6px rgba(0,0,0,0.08)',
                    opacity: isDragging ? 0.5 : (item.active ? 1 : 0.55),
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    position: 'relative', transition: 'all 0.15s'
                }}
            >
                {options.showRemove && isAdmin && hasEvo && (
                    <Tooltip title="Quitar evolución">
                        <span
                            onClick={(e) => { e.stopPropagation(); handleSetEquipEvolution(type, item, null); }}
                            style={{
                                position: 'absolute', top: -8, right: -8, width: 20, height: 20,
                                borderRadius: '50%', background: '#ff4d4f', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, cursor: 'pointer', zIndex: 2, lineHeight: 1
                            }}
                        >×</span>
                    </Tooltip>
                )}
                <div
                    onClick={(e) => { e.stopPropagation(); if (isAdmin) openEquipmentModal(type, item); }}
                    title={isAdmin ? `Editar ${item.nom}` : item.nom}
                    style={{ cursor: isAdmin ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <img src={javamonImage(item)} alt={item.nom} width={imgSize} height={imgSize} style={{ objectFit: 'contain', pointerEvents: 'none' }} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', lineHeight: 1.1, wordBreak: 'break-word' }}>{item.nom}</div>
                {item.ident && <div style={{ fontSize: 9, color: '#999' }}>{item.ident}</div>}
            </div>
        );
    };

    const renderEquipGrid = (type: string) => {
        const items = equipListForType(type);
        return (
            <Card size="small" title={`${items.length} elementos`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, maxWidth: 320 }}>
                    <span style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>Tamaño:</span>
                    <Slider
                        min={40}
                        max={160}
                        step={4}
                        value={equipCardSize}
                        onChange={(v) => setEquipCardSize(v as number)}
                        style={{ flex: 1 }}
                    />
                </div>
                {type === 'skin' && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, fontSize: 12 }}>
                        <span style={{ color: '#8c8c8c' }}>Contorno según clase:</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 2, background: '#f5222d', display: 'inline-block' }} /> Guerrero</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 2, background: '#52c41a', display: 'inline-block' }} /> Arquero</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 2, background: '#1890ff', display: 'inline-block' }} /> Mago</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 2, background: '#fa8c16', display: 'inline-block' }} /> Luchador</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 2, background: '#e0e0e0', display: 'inline-block' }} /> Aventurero/Otro</span>
                    </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {items.length === 0
                        ? <span style={{ color: '#aaa' }}>No hay elementos.</span>
                        : items.map((it: any) => renderEquipCard(type, it, { size: equipCardSize }))}
                </div>
            </Card>
        );
    };

    const renderEquipEvolutionGrid = (type: string) => {
        const items = equipListForType(type);
        const byIdent: Record<string, any> = {};
        items.forEach((it: any) => { if (it.ident) byIdent[it.ident] = it; });

        const isTarget: Record<string, boolean> = {};
        items.forEach((it: any) => {
            if (it.evoluciona_a && it.evoluciona_a !== 'no evoluciona') isTarget[it.evoluciona_a] = true;
        });

        const chains: any[][] = [];
        const usedInChain = new Set<string>();
        items.forEach((it: any) => {
            const hasEvo = it.evoluciona_a && it.evoluciona_a !== 'no evoluciona';
            const isRoot = !isTarget[it.ident];
            if (isRoot && hasEvo) {
                const chain: any[] = [];
                let current: any = it;
                const guard = new Set<string>();
                while (current && !guard.has(current.ident)) {
                    guard.add(current.ident);
                    chain.push(current);
                    usedInChain.add(current.ident);
                    const nextIdent = (current.evoluciona_a && current.evoluciona_a !== 'no evoluciona') ? current.evoluciona_a : null;
                    current = nextIdent ? byIdent[nextIdent] : null;
                }
                if (chain.length > 1) chains.push(chain);
            }
        });

        const singles = items.filter((it: any) => !usedInChain.has(it.ident));

        return (
            <div>
                <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8, fontSize: 13 }}>
                    🧬 <b>Evoluciones:</b> arrastra un elemento <b>encima de otro</b> para que el primero evolucione en el segundo.
                    Pulsa la <b style={{ color: '#ff4d4f' }}>×</b> para quitar la evolución. Click en la imagen para editar.
                </div>

                {chains.length > 0 && (
                    <Card size="small" title={`Cadenas de evolución (${chains.length})`} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {chains.map((chain, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {chain.map((it: any, i: number) => (
                                        <React.Fragment key={it.id}>
                                            {renderEquipCard(type, it, { enableEvolutionDrop: true, showRemove: true })}
                                            {i < chain.length - 1 && <span style={{ fontSize: 22, color: '#1890ff', fontWeight: 'bold' }}>→</span>}
                                        </React.Fragment>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                <Card size="small" title={`Sin cadena (${singles.length})`}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {singles.length === 0
                            ? <span style={{ color: '#aaa' }}>No hay elementos sueltos.</span>
                            : singles.map((it: any) => renderEquipCard(type, it, { enableEvolutionDrop: true }))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderEquipTab = (type: string, tableNode: React.ReactNode) => {
        const view = equipView[type] || 'table';
        const options = [
            { value: 'table', label: '📋 Vista Tabla' },
            { value: 'grid', label: '🖼️ Vista Cuadrícula' },
            ...(equipHasEvolution(type) ? [{ value: 'evo', label: '🧬 Vista Evoluciones' }] : []),
            { value: 'usage', label: '🎁 Vista de Uso / Recompensas' }
        ];
        return (
            <div>
                <div style={{ marginBottom: 12 }}>
                    <Select
                        value={view}
                        onChange={(v) => setEquipView((prev) => ({ ...prev, [type]: v }))}
                        style={{ width: 240 }}
                        options={options}
                    />
                </div>
                {view === 'table' && tableNode}
                {view === 'grid' && renderEquipGrid(type)}
                {view === 'evo' && equipHasEvolution(type) && renderEquipEvolutionGrid(type)}
                {view === 'usage' && (
                    <RewardsUsageView
                        type={type}
                        items={equipListForType(type)}
                        rewardsUsage={rewardsUsage}
                        loading={loading}
                        loadingRewardsUsage={loadingRewardsUsage}
                        fetchRewardsUsage={fetchRewardsUsage}
                    />
                )}
            </div>
        );
    };

    return (
        <div>
            <Tabs defaultActiveKey="skin" type="line">
                <TabPane tab="Skins" key="skin">
                    <Space style={{ marginBottom: 16 }}>
                        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipmentModal('skin')}>
                            Nuevo Skin
                        </Button>}
                        <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>Refrescar</Button>
                    </Space>
                    {renderEquipTab('skin', <Table dataSource={skinsConfig.skins} columns={columnsSkin} loading={loading} rowKey="id" />)}
                </TabPane>
                <TabPane tab="Armas" key="arma">
                    <Space style={{ marginBottom: 16 }}>
                        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipmentModal('arma')}>
                            Nueva Arma
                        </Button>}
                        <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>Refrescar</Button>
                    </Space>
                    {renderEquipTab('arma', <Table dataSource={skinsConfig.armes} columns={columnsArma} loading={loading} rowKey="id" />)}
                </TabPane>
                <TabPane tab="Escudos" key="escut">
                    <Space style={{ marginBottom: 16 }}>
                        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipmentModal('escut')}>
                            Nuevo Escudo
                        </Button>}
                        <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>Refrescar</Button>
                    </Space>
                    {renderEquipTab('escut', <Table dataSource={skinsConfig.escuts} columns={columnsEscut} loading={loading} rowKey="id" />)}
                </TabPane>
                <TabPane tab="Arcos" key="arc">
                    <Space style={{ marginBottom: 16 }}>
                        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipmentModal('arc')}>
                            Nuevo Arco
                        </Button>}
                        <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>Refrescar</Button>
                    </Space>
                    {renderEquipTab('arc', <Table dataSource={skinsConfig.arcs} columns={columnsArc} loading={loading} rowKey="id" />)}
                </TabPane>
                <TabPane tab="Flechas" key="flecha">
                    <Space style={{ marginBottom: 16 }}>
                        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipmentModal('flecha')}>
                            Nueva Flecha
                        </Button>}
                        <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>Refrescar</Button>
                    </Space>
                    {renderEquipTab('flecha', <Table dataSource={skinsConfig.flechas} columns={columnsFlecha} loading={loading} rowKey="id" />)}
                </TabPane>
                <TabPane tab="Magias" key="magia">
                    <Space style={{ marginBottom: 16 }}>
                        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipmentModal('magia')}>
                            Nueva Magia
                        </Button>}
                        <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>Refrescar</Button>
                    </Space>
                    {renderEquipTab('magia', <Table dataSource={skinsConfig.magias} columns={columnsMagia} loading={loading} rowKey="id" />)}
                </TabPane>
            </Tabs>

            {/* Modal Equipamiento */}
            <Modal
                title={editingItem ? `Editar ${equipmentType}` : `Crear ${equipmentType}`}
                open={equipmentModal}
                onCancel={() => setEquipmentModal(false)}
                onOk={() => form.submit()}
                destroyOnClose
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSaveEquipment}
                    initialValues={{ active: true, velocitat: 0, salut: 0, defensa: 0, dany: 0, rang: 0 }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item name="nom" label="Nombre" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="ident" label="Identificador Unity" rules={[{ required: true }]}>
                            <Input placeholder="Ej: Skin_01" />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {equipmentType === 'skin' && (
                            <>
                                <Form.Item name="velocitat" label="Velocidad">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="salut" label="Salud">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="defensa" label="Defensa">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="type" label="Clase (Tipo)" initialValue="Aventurero">
                                    <Select>
                                        <Select.Option value="Aventurero">Aventurero (Ninguno)</Select.Option>
                                        <Select.Option value="Guerrero">Guerrero</Select.Option>
                                        <Select.Option value="Arquero">Arquero</Select.Option>
                                        <Select.Option value="Mago">Mago</Select.Option>
                                        <Select.Option value="Luchador">Luchador</Select.Option>
                                    </Select>
                                </Form.Item>
                            </>
                        )}

                        {equipmentType === 'arma' && (
                            <>
                                <Form.Item name="dany" label="Daño">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="critico" label="Crítico (%)" initialValue={0}>
                                    <InputNumber style={{ width: '100%' }} min={0} max={100} />
                                </Form.Item>
                                <Form.Item name="velocidad" label="Velocidad (0-10)" initialValue={5}>
                                    <InputNumber style={{ width: '100%' }} min={0} max={10} />
                                </Form.Item>
                            </>
                        )}

                        {equipmentType === 'escut' && (
                            <>
                                <Form.Item name="defensa" label="Defensa">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="bloqueo" label="Bloqueo % (0-100)">
                                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="regeneracion" label="Regeneración (0-10)">
                                    <InputNumber min={0} max={10} style={{ width: '100%' }} />
                                </Form.Item>
                            </>
                        )}

                        {equipmentType === 'arc' && (
                            <>
                                <Form.Item name="dany" label="Daño">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="rang" label="Rango">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="velocidad" label="Velocidad Ataque">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                            </>
                        )}

                        {(equipmentType === 'arma' || equipmentType === 'escut' || equipmentType === 'arc') && (
                            <Form.Item name="evoluciona_a" label="Evoluciona a">
                                <Select allowClear placeholder="Selecciona evolución (opcional)">
                                    <Select.Option value="no evoluciona">No evoluciona</Select.Option>
                                    {equipmentType === 'arma' && skinsConfig.armes.map((x: any) => <Select.Option key={x.ident} value={x.ident}>{x.nom} ({x.ident})</Select.Option>)}
                                    {equipmentType === 'escut' && skinsConfig.escuts.map((x: any) => <Select.Option key={x.ident} value={x.ident}>{x.nom} ({x.ident})</Select.Option>)}
                                    {equipmentType === 'arc' && skinsConfig.arcs.map((x: any) => <Select.Option key={x.ident} value={x.ident}>{x.nom} ({x.ident})</Select.Option>)}
                                </Select>
                            </Form.Item>
                        )}

                        {equipmentType === 'flecha' && (
                            <>
                                <Form.Item name="dany" label="Daño">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="critico" label="Crítico % (0-100)">
                                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                </Form.Item>
                            </>
                        )}

                        {equipmentType === 'magia' && (
                            <>
                                <Form.Item name="dany" label="Daño">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="rang" label="Rango">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="tipo" label="Tipo de Magia" initialValue="proyectil">
                                    <Select>
                                        <Select.Option value="proyectil">Proyectil</Select.Option>
                                        <Select.Option value="estatica">Estática</Select.Option>
                                        <Select.Option value="area">Área</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name="elemento" label="Elemento" initialValue="">
                                    <Select allowClear placeholder="Selecciona un elemento">
                                        <Select.Option value="">Ninguno</Select.Option>
                                        <Select.Option value="fuego">Fuego</Select.Option>
                                        <Select.Option value="viento">Viento</Select.Option>
                                        <Select.Option value="rayo">Rayo</Select.Option>
                                        <Select.Option value="agua">Agua</Select.Option>
                                        <Select.Option value="tierra">Tierra</Select.Option>
                                        <Select.Option value="humo">Humo</Select.Option>
                                        <Select.Option value="veneno">Veneno</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name="es_escudo" label="Tipo de Habilidad" initialValue="magia">
                                    <Select>
                                        <Select.Option value="magia">Magia Normal</Select.Option>
                                        <Select.Option value="escudo">Escudo (protege sin daño)</Select.Option>
                                        <Select.Option value="golpe">Golpe (cuerpo a cuerpo)</Select.Option>
                                    </Select>
                                </Form.Item>
                            </>
                        )}
                    </div>

                    <Form.Item label="Subir imagen/icono">
                        <Upload
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={async ({ fileList }) => {
                                setFileList(fileList);
                                if (fileList.length > 0 && fileList[0].originFileObj) {
                                    const b64 = await getBase64(fileList[0].originFileObj);
                                    setPreviewImage(b64);
                                    const fileName = fileList[0].name || '';
                                    const identFromFile = fileName.replace(/\.[^/.]+$/, '');
                                    if (identFromFile) {
                                        form.setFieldsValue({ ident: identFromFile });
                                    }
                                } else {
                                    setPreviewImage(null);
                                }
                            }}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
                        </Upload>
                        {(previewImage || editingItem) && (
                            <div style={{ marginTop: 10 }}>
                                <Image
                                    src={previewImage || getImageUrl(editingItem, equipmentType || 'javamon')}
                                    alt="preview"
                                    width={100}
                                    style={{ borderRadius: 8, border: '1px solid #d9d9d9', padding: 4 }}
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item name="active" label="Activo" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
